import { type FormikProps, FormikProvider } from 'formik';

import { FormikInput } from '@/components/forms/FormikInput';
import { FormikSelect } from '@/components/forms/FormikSelect';
import { FormikTextArea } from '@/components/forms/FormikTextArea';
import { Button, Label, SelectItem } from '@/design-system/components/ui';

import {
  type AffiliateIdentifierType,
  getIdentifierLabel,
  getIdentifierPlaceholder,
  IDENTIFIER_TYPE_OPTIONS,
} from '../domain/affiliateIdentifier';
import {
  type AffiliateRegion,
  type AffiliateStatus,
  type OrganicTier,
  REGION_OPTIONS,
  type TierProtection,
} from '../domain/types';
import { type PendingProtection, TierProtectionCard } from './TierProtectionCard';

export interface AffiliateFormValues {
  user_identifier?: string;
  user_identifier_type?: AffiliateIdentifierType;
  alias: string;
  region: string;
  status: string;
  note: string;
}

interface TierOption {
  id: string;
  name: string;
  rank: number;
  audienceId: string | null;
  requiresManualProjectApproval: boolean;
}

interface AffiliateFormProps {
  mode: 'create' | 'edit';
  projectId: string;
  formik: FormikProps<AffiliateFormValues>;
  identifier?: string;
  identifierType?: string;
  organicTier: OrganicTier | null;
  currentTier: OrganicTier | null;
  protections: TierProtection[];
  tiers: TierOption[];
  onPendingProtectionChange: (pending: PendingProtection | null, clearing: boolean, incomplete: boolean) => void;
  onCancel: () => void;
  submitLabel: string;
  submittingLabel: string;
  isSubmitting: boolean;
  isProtectionLoading: boolean;
  submitDisabled?: boolean;
}

export const STATUS_OPTIONS: AffiliateStatus[] = ['Active', 'Paused', 'Terminated', 'Flagged'];

export function AffiliateForm({
  mode,
  projectId,
  formik,
  identifier,
  identifierType,
  organicTier,
  currentTier,
  protections,
  tiers,
  onPendingProtectionChange,
  onCancel,
  submitLabel,
  submittingLabel,
  isSubmitting,
  isProtectionLoading,
  submitDisabled,
}: AffiliateFormProps) {
  const formIdentifierType = formik.values.user_identifier_type;
  const displayIdentifierType = identifierType ?? formIdentifierType;
  const hasAnyTier = Boolean(currentTier || organicTier || protections.length > 0);
  const addProtectionLabel = mode === 'create' || !hasAnyTier ? 'Assign tier manually' : undefined;

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit} className="tw:flex tw:flex-col tw:gap-6">
        {mode === 'create' ? (
          <>
            <FormikSelect name="user_identifier_type" label="Identifier Type">
              {IDENTIFIER_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </FormikSelect>

            <FormikInput
              name="user_identifier"
              label={getIdentifierLabel(formIdentifierType)}
              placeholder={getIdentifierPlaceholder(formIdentifierType)}
            />
          </>
        ) : (
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <Label>{getIdentifierLabel(displayIdentifierType)}</Label>
            <div className="tw:rounded-md tw:border tw:border-input tw:bg-muted tw:px-3 tw:py-2 tw:text-sm tw:font-mono tw:text-muted-foreground tw:break-all">
              {identifier}
            </div>
          </div>
        )}

        <FormikInput name="alias" label="Alias (display name)" placeholder="e.g. CryptoInfluencer" />

        <FormikSelect name="region" label="Region">
          {REGION_OPTIONS.map((region: AffiliateRegion) => (
            <SelectItem key={region} value={region}>
              {region}
            </SelectItem>
          ))}
        </FormikSelect>

        <FormikSelect name="status" label="Status">
          {STATUS_OPTIONS.map((status) => (
            <SelectItem key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </SelectItem>
          ))}
        </FormikSelect>

        <FormikTextArea
          name="note"
          label="Notes"
          placeholder="e.g. Top performer from Q1 campaign, handles LATAM region"
        />

        <TierProtectionCard
          projectId={projectId}
          organicTier={organicTier}
          currentTier={currentTier}
          protections={protections}
          tiers={tiers}
          onPendingChange={onPendingProtectionChange}
          isLoading={isProtectionLoading}
          addProtectionLabel={addProtectionLabel}
          addProtectionHelper={
            mode === 'create'
              ? "If you don't assign a tier manually, the affiliate will be placed automatically based on audience conditions."
              : undefined
          }
        />

        <div className="tw:flex tw:gap-2 tw:mt-2 tw:justify-end">
          <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSubmitting || submitDisabled}>
            {isSubmitting ? submittingLabel : submitLabel}
          </Button>
        </div>
      </form>
    </FormikProvider>
  );
}
